import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreVertical, Eye, Coins, UserCheck, UserX, ExternalLink } from 'lucide-react';
import { safeFormat } from '../../utils/safeFormat';
import { Button } from '../common/Button';
import { User, GTWallet, GTTransaction, Report, StoreRedemption } from '../../types';

// Extended transaction type with userId
export interface ExtendedTransaction extends GTTransaction {
  userId: string;
}

// User Row Component
interface UserRowProps {
  user: User;
  wallet: GTWallet | undefined;
  onBan: (user: User) => void;
  onUnban: (user: User) => void;
  onAdjustGT: (user: User) => void;
  onViewProfile: (user: User) => void;
}

export const UserRow: React.FC<UserRowProps> = ({
  user,
  wallet,
  onBan,
  onUnban,
  onAdjustGT,
  onViewProfile
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const handleMenuToggle = () => {
    if (!menuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 192;
      // Calculate left position, ensuring it doesn't go off screen
      let left = rect.left;
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 16;
      }
      if (left < 16) {
        left = 16;
      }
      setMenuPosition({
        top: rect.bottom + 4,
        left: left
      });
    }
    setMenuOpen(!menuOpen);
  };

  const dropdownMenu = ReactDOM.createPortal(
    <AnimatePresence>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setMenuOpen(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            style={{ top: menuPosition.top, left: menuPosition.left }}
            className="fixed w-48 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-border-light dark:border-border-dark z-[9999] overflow-hidden"
          >
            <button
              onClick={() => { onViewProfile(user); setMenuOpen(false); }}
              className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-light dark:hover:bg-gray-800 flex items-center gap-2 transition-colors text-text-primary-light dark:text-text-primary-dark"
            >
              <Eye size={16} className="text-text-secondary-light dark:text-text-secondary-dark" />
              View Profile
            </button>
            <button
              onClick={() => { onAdjustGT(user); setMenuOpen(false); }}
              className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-light dark:hover:bg-gray-800 flex items-center gap-2 transition-colors text-text-primary-light dark:text-text-primary-dark"
            >
              <Coins size={16} className="text-primary" />
              Adjust GT
            </button>
            {user.isBanned ? (
              <button
                onClick={() => { onUnban(user); setMenuOpen(false); }}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-light dark:hover:bg-gray-800 flex items-center gap-2 text-success transition-colors"
              >
                <UserCheck size={16} />
                Unban User
              </button>
            ) : (
              <button
                onClick={() => { onBan(user); setMenuOpen(false); }}
                className="w-full px-4 py-2.5 text-left text-sm hover:bg-surface-light dark:hover:bg-gray-800 flex items-center gap-2 text-error transition-colors"
              >
                <UserX size={16} />
                Ban User
              </button>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );

  return (
    <tr className="border-b border-border-light dark:border-border-dark hover:bg-surface-light/50 dark:hover:bg-gray-800/50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <img src={user.photoURL} alt={user.username} className="w-9 h-9 rounded-full object-cover" />
          <div>
            <p className="font-medium text-sm">{user.displayName}</p>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">@{user.username}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm">{user.email}</p>
      </td>
      <td className="px-4 py-3">
        <span className="font-medium text-primary text-sm">{wallet?.balance.toLocaleString() ?? 0} GT</span>
      </td>
      <td className="px-4 py-3">
        {user.isAdmin && <span className="badge-primary text-xs">Admin</span>}
        {user.isBanned && <span className="badge-error text-xs">Banned</span>}
        {!user.isAdmin && !user.isBanned && <span className="badge-success text-xs">Active</span>}
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          {safeFormat(user.createdAt, 'MMM d, yyyy')}
        </p>
      </td>
      <td className="px-4 py-3">
        <button
          ref={buttonRef}
          onClick={handleMenuToggle}
          className="p-1.5 hover:bg-surface-light dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <MoreVertical size={16} />
        </button>
        {dropdownMenu}
      </td>
    </tr>
  );
};

// Transaction Row Component
interface TransactionRowProps {
  transaction: ExtendedTransaction;
  user: User | undefined;
  onViewUser: (user: User) => void;
}

export const TransactionRow: React.FC<TransactionRowProps> = ({
  transaction,
  user,
  onViewUser
}) => {
  const typeColors: Record<string, string> = {
    purchase: 'badge-success',
    admin_adjustment: 'badge-warning',
    spend_store: 'badge-error',
    spend_tip: 'badge-info',
    earn_tip: 'badge-success',
    earn_slip: 'badge-success',
    earn_trivia: 'badge-success',
    earn_streak: 'badge-success',
    earn_engagement: 'badge-success',
    refund: 'badge-warning'
  };

  const stripeLink = transaction.metadata?.stripePaymentIntentId
    ? `https://dashboard.stripe.com/payments/${transaction.metadata.stripePaymentIntentId}`
    : null;

  // Check if this is a revenue-generating transaction
  const isPurchase = transaction.type === 'purchase';
  const revenueAmount = isPurchase && transaction.metadata?.amountCents
    ? (transaction.metadata.amountCents / 100).toFixed(2)
    : null;

  return (
    <tr className="border-b border-border-light dark:border-border-dark hover:bg-surface-light/50 dark:hover:bg-gray-800/50 transition-colors">
      <td className="px-4 py-3">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          {safeFormat(transaction.createdAt, 'MMM d, yyyy HH:mm')}
        </p>
      </td>
      <td className="px-4 py-3">
        {user ? (
          <button
            onClick={() => onViewUser(user)}
            className="flex items-center gap-2 hover:text-primary transition-colors"
          >
            <img src={user.photoURL} alt={user.username} className="w-7 h-7 rounded-full object-cover" />
            <span className="text-sm font-medium">@{user.username}</span>
          </button>
        ) : (
          <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            {transaction.userId.slice(0, 8)}...
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`badge text-xs ${typeColors[transaction.type] || 'badge-secondary'}`}>
          {transaction.type.replace(/_/g, ' ')}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`font-medium text-sm ${transaction.amount >= 0 ? 'text-success' : 'text-error'}`}>
          {transaction.amount >= 0 ? '+' : ''}{transaction.amount.toLocaleString()} GT
        </span>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark truncate max-w-[200px]">
          {transaction.description || 'No description'}
        </p>
      </td>
      <td className="px-4 py-3">
        {revenueAmount ? (
          <span className="text-sm font-medium text-success">
            £{revenueAmount}
          </span>
        ) : (
          <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            £0.00
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        {stripeLink ? (
          <a
            href={stripeLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Stripe <ExternalLink size={12} />
          </a>
        ) : (
          <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            N/A
          </span>
        )}
      </td>
    </tr>
  );
};

// Report Row Component
interface ReportRowProps {
  report: Report;
  onResolve: (report: Report) => void;
}

export const ReportRow: React.FC<ReportRowProps> = ({ report, onResolve }) => {
  const statusColors = {
    pending: 'badge-warning',
    reviewing: 'badge-info',
    resolved: 'badge-success',
    dismissed: 'badge-secondary'
  };

  return (
    <tr className="border-b border-border-light dark:border-border-dark hover:bg-surface-light/50 dark:hover:bg-gray-800/50 transition-colors">
      <td className="px-4 py-3">
        <span className={`badge text-xs ${statusColors[report.status as keyof typeof statusColors] || 'badge-secondary'}`}>
          {report.status}
        </span>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm">{report.contentType}</p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm">{report.reason}</p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          {report.reportedByUsername}
        </p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          {safeFormat(report.createdAt, 'MMM d, yyyy')}
        </p>
      </td>
      <td className="px-4 py-3">
        {report.status === 'pending' && (
          <Button size="sm" variant="outline" onClick={() => onResolve(report)}>
            Review
          </Button>
        )}
      </td>
    </tr>
  );
};

// Redemption Row Component
interface RedemptionRowProps {
  redemption: StoreRedemption;
  onFulfill: (redemption: StoreRedemption) => void;
}

export const RedemptionRow: React.FC<RedemptionRowProps> = ({ redemption, onFulfill }) => {
  const statusColors = {
    pending: 'badge-warning',
    processing: 'badge-info',
    fulfilled: 'badge-success',
    cancelled: 'badge-error'
  };

  return (
    <tr className="border-b border-border-light dark:border-border-dark hover:bg-surface-light/50 dark:hover:bg-gray-800/50 transition-colors">
      <td className="px-4 py-3">
        <span className={`badge text-xs ${statusColors[redemption.status as keyof typeof statusColors] || 'badge-secondary'}`}>
          {redemption.status}
        </span>
      </td>
      <td className="px-4 py-3">
        <div>
          <p className="font-medium text-sm">{redemption.itemName}</p>
          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            {redemption.category}
          </p>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="font-medium text-primary text-sm">
          {redemption.gtSpent.toLocaleString()} GT
        </span>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          {redemption.username}
        </p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          {safeFormat(redemption.createdAt, 'MMM d, yyyy')}
        </p>
      </td>
      <td className="px-4 py-3">
        {redemption.status === 'pending' && (
          <Button size="sm" variant="primary" onClick={() => onFulfill(redemption)}>
            Fulfill
          </Button>
        )}
      </td>
    </tr>
  );
};
